/* eslint-disable no-use-before-define, camelcase */

import { useEffect } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { create } from 'zustand';
import socketIO from 'socket.io-client';
import sailsIO from 'sails.io.js';
import store from 'app-store';
import _ from 'lodash';
import moment from 'moment';
import createUuid from 'uuid/v4';

// Actions
import { alerts, clearAlerts } from 'modules/alerts/actions';

const loadTime = moment();
const io = sailsIO(socketIO);

// Required config for multi endpoint
io.sails.transports = ['websocket'];

// If the socket disconnects, let the user know so they're not operating with out of date data
io.socket.on('disconnect', () => {
  alerts.error({ title: 'Server connection lost', subtitle: 'Refresh the page' });
});

// If the socket reconnects, reregister the socket listeners and let the user know they are connected again
io.socket.on('reconnect', reconnectAllSockets);

const debouncedReconnectAllSockets = _.debounce(reconnectAllSockets, 30 * 60 * 1000, { leading: true, trailing: false });

document.addEventListener('visibilitychange', () => {
  const isVisible = document.visibilityState === 'visible';
  const loadedRecently = moment().subtract(30, 'minutes').isBefore(loadTime);

  if (isVisible && !loadedRecently) debouncedReconnectAllSockets(true);
});

export function reconnectAllSockets(skipAlert) {
  const { checkRegister, sockets } = useSocketStore.getState();

  _.forEach(_.keys(sockets), socketName => {
    unstable_batchedUpdates(() => {
      checkRegister(socketName, true); // Reregister all current socket listeners
      onReconnectSocket(socketName);
    });
  });
  
  clearAlerts();
  if (!skipAlert) alerts.success({ title: 'Reconnected to the server!', expires: 3 });
}

// The raw io.socket handler for each socket name, attached at most once ever.
// socket.io reuses the same underlying Socket across reconnects, so a handler
// survives a disconnect — re-attaching on reconnect only duplicates it, and a
// duplicated handler multiplies the work done for every inbound message.
const rawHandlers = {};

function attachRawHandler(socketName) {
  if (rawHandlers[socketName]) return;

  rawHandlers[socketName] = data => onUpdateSocket(socketName, data);
  io.socket.on(socketName, rawHandlers[socketName]);
}

function register(socketName) {
  // The server drops subscriptions on disconnect, so this POST does have to be
  // re-sent on every reconnect — unlike the handler attached below.
  io.socket.request({
    method: 'POST',
    url: '/api/socket/register',
    headers: { Authorization: `Bearer ${store.getState().auth.token}` },
    data: { socket: socketName }
  }, (resData) => {
    if (process.env.NODE_ENV !== 'production') console.log(`${socketName} socket: ${resData}`);
  });

  attachRawHandler(socketName);
}

function deregister(socketName) {
  io.socket.request({
    method: 'POST',
    url: '/api/socket/deregister',
    headers: { Authorization: `Bearer ${store.getState().auth.token}` },
    data: { socket: socketName }
  });
}

function onUpdateSocket(socketName, data) {
  // console.log(`-------------${socketName}--------------`, data);

  // Guarded because the raw handler now outlives any individual registration —
  // it can fire for a socket name with no store entry.
  _.forEach(_.get(useSocketStore.getState(), ['sockets', socketName, 'onUpdate']), listener => {
    listener(data);
  });
}

function onReconnectSocket(socketName) {
  _.forEach(useSocketStore.getState().sockets[socketName].onReconnect, listener => {
    listener();
  });
}

const useSocketStore = create((set) => ({
  sockets: {},
  startListening: (socketName, listenerKey, onUpdate, onReconnect) => set(state => {
    const sockets = state.sockets || {};
    const socket = sockets[socketName] || { onUpdate: {}, onReconnect: {}, connected: false };

    if (onUpdate) socket.onUpdate[listenerKey] = onUpdate;
    if (onReconnect) socket.onReconnect[listenerKey] = onReconnect;

    return { sockets: { ...sockets, [socketName]: socket } };
  }),
  stopListening: (socketName, listenerKey) => set(state => {
    const sockets = state.sockets || {};
    const socket = sockets[socketName];

    if (!socket) return;

    delete socket.onUpdate[listenerKey];
    delete socket.onReconnect[listenerKey];

    return { sockets: { ...sockets, [socketName]: socket } };
  }),
  checkRegister: (socketName, isReconnect = false) => set(state => {
    const sockets = state.sockets || {};
    const socket = sockets[socketName];
    const hasListeners = socket && _.size(socket.onUpdate);
    let connected = socket && socket.connected;

    if (!socket) return;

    if ((!connected || isReconnect) && hasListeners) {
      register(socketName);
      connected = true;
    }

    if (connected && !hasListeners) {
      deregister(socketName);
      connected = false;
    }

    return { sockets: { ...sockets, [socketName]: { ...socket, connected } } };
  })
}));

useSocketStore.subscribe((state, oldState) => {
  const { sockets, checkRegister } = state;
  const { sockets: oldSockets } = oldState;

  _.forEach(sockets, (socket, socketName) => {
    const oldCount = _.size(_.get(oldSockets, `${socketName}.onUpdate`, {}));
    const newCount = _.size(socket.onUpdate);

    if (oldCount !== newCount) checkRegister(socketName);
  });
});

export function useSocket(socketName, onSocketUpdate, onSocketReconnect, enabled = true) {
  const { startListening, stopListening } = useSocketStore();

  useEffect(() => {
    if (!enabled) return undefined;

    const key = createUuid();
    startListening(socketName, key, onSocketUpdate, onSocketReconnect);

    return () => {
      stopListening(socketName, key);
    };
  }, [onSocketUpdate, enabled]);
}

export function registerSocket(socketName, onSocketUpdate, onSocketReconnect) {
  const { startListening, stopListening } = useSocketStore.getState();
  const key = createUuid();

  unstable_batchedUpdates(() => {
    startListening(socketName, key, onSocketUpdate, onSocketReconnect);
  });

  return () => {
    unstable_batchedUpdates(() => {
      stopListening(socketName, key);
    });
  };
}
